import { FC, useCallback, useEffect, useState } from 'react';

import { EmbeddedMessage } from 'constants/messageMediums';
import { Button, EmbeddedContentDefinition } from 'modules/graphql/types';
import { useGetTemplateUserJsonTestData } from 'modules/templateEditor/hooks/useGetTemplateUserJsonTestData';

import { PreviewWithDataErrorMessage } from '../../common/types';
import {
  getContentWithData,
  getPreviewWithDataError,
  getTemplateHasContent,
} from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { EmbeddedPreviewContext, EmbeddedPreviewContextState } from './EmbeddedPreviewContext';

export const EmbeddedPreviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { liveTemplate } = useEditableFieldsContext();
  const embeddedData = (liveTemplate?.mediumData || {}) as EmbeddedContentDefinition;
  const { canRefreshPreview, dataFeedData, userData, isUserDataDirty, setIsUserDataDirty } =
    usePreviewWithDataContext();
  const { templateId } = useQueryParamContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [mediaUrl, setMediaUrl] = useState<string>('');
  const [actionButtons, setActionButtons] = useState<Button[]>([]);
  const [previewError, setPreviewError] = useState<PreviewWithDataErrorMessage | undefined>(
    undefined,
  );

  const hasContent = getTemplateHasContent(EmbeddedMessage, embeddedData);
  const payload = embeddedData.payload || '{}';
  const locale = embeddedData?.locale?.name || '';

  const { getTemplateUserJsonTestData } = useGetTemplateUserJsonTestData();

  const onRefreshPreview = useCallback(async () => {
    // Only make request when template data has been received
    if (canRefreshPreview && hasContent) {
      const userDataToSave = await getTemplateUserJsonTestData(userData, isUserDataDirty);

      setIsLoading(true);
      getContentWithData(
        templateId,
        {
          subject: '',
          embeddedTitle: embeddedData.title || '',
          embeddedBody: embeddedData.body || '',
          embeddedMediaUrl: embeddedData?.elements?.mediaUrl || '',
          embeddedActionButtons: embeddedData.elements?.buttons || [],
          payload: JSON.parse(payload),
        },
        JSON.parse(dataFeedData),
        JSON.parse(userDataToSave),
      )
        .then(({ data }) => {
          setIsUserDataDirty(false);
          setTitle(data.embeddedTitle);
          setBody(data.embeddedBody);
          setMediaUrl(data.embeddedMediaUrl);
          setActionButtons(data.embeddedActionButtons);
          setPreviewError(undefined);
        })
        .catch(({ response }) => {
          setPreviewError(getPreviewWithDataError(response?.data?.message || ''));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    canRefreshPreview,
    dataFeedData,
    templateId,
    userData,
    hasContent,
    embeddedData,
    isUserDataDirty,
    setIsUserDataDirty,
    getTemplateUserJsonTestData,
  ]);

  /* Note: we only want this useEffect to run when dataFeedData or userData change even though it is
   * also dependent on onRefreshPreview. However, onRefreshPreview shares ALL of the useEffect's
   * dependencies, so the version of onRefreshPreview used will always be current.
   * Including onRefreshPreview as a dependency here would cause an unintended side-effect of
   * running onRefreshPreview on each change of any content */
  useEffect(() => {
    if (canRefreshPreview) {
      onRefreshPreview();
    }
  }, [canRefreshPreview, dataFeedData, userData, locale]);

  const context: EmbeddedPreviewContextState = {
    isLoading,
    title,
    body,
    mediaUrl,
    actionButtons,
    hasContent,
    previewError,
    onRefreshPreview,
  };

  return (
    <EmbeddedPreviewContext.Provider value={context}>{children}</EmbeddedPreviewContext.Provider>
  );
};
